import { useCallback, useEffect, useRef, useState } from "react";

const WS_URL = import.meta.env.VITE_WS_URL;
const MAX_RECONNECT_ATTEMPTS = 5;
const HEARTBEAT_INTERVAL = 30000;

export interface WSMessage {
  type: string;
  payload?: unknown;
}

export type MessageHandler = (message: WSMessage) => void;

export interface UnifiedWebSocketAPI {
  isConnected: boolean;
  sendMessage: (message: WSMessage) => void;
  subscribe: (channels: string[]) => void;
  unsubscribe: (channels: string[]) => void;
  addMessageListener: (handler: MessageHandler) => () => void;
}

interface UseUnifiedWebSocketOptions {
  userId?: string;
  userRole?: string;
}

export function useUnifiedWebSocket(
  options: UseUnifiedWebSocketOptions
): UnifiedWebSocketAPI {
  const { userId, userRole } = options;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const listenersRef = useRef<Set<MessageHandler>>(new Set());
  const activeChannelsRef = useRef<Set<string>>(new Set());

  const [isConnected, setIsConnected] = useState(false);

  const clearHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const sendMessage = useCallback((message: WSMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  const subscribe = useCallback(
    (channels: string[]) => {
      channels.forEach((ch) => activeChannelsRef.current.add(ch));
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({ type: "subscribe", payload: { channels } })
        );
      }
    },
    []
  );

  const unsubscribe = useCallback(
    (channels: string[]) => {
      channels.forEach((ch) => activeChannelsRef.current.delete(ch));
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({ type: "unsubscribe", payload: { channels } })
        );
      }
    },
    []
  );

  const addMessageListener = useCallback((handler: MessageHandler) => {
    listenersRef.current.add(handler);
    return () => {
      listenersRef.current.delete(handler);
    };
  }, []);

  const connect = useCallback(() => {
    if (!userId || !WS_URL) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(`${WS_URL}?userId=${userId}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;

        // Auth handshake
        ws.send(
          JSON.stringify({
            type: "auth",
            payload: { userId, userRole },
          })
        );

        // Re-subscribe to any active channels
        const channels = Array.from(activeChannelsRef.current);
        if (channels.length > 0) {
          ws.send(
            JSON.stringify({ type: "subscribe", payload: { channels } })
          );
        }

        // Start heartbeat
        clearHeartbeat();
        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "PING" }));
          }
        }, HEARTBEAT_INTERVAL);
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const message = JSON.parse(event.data) as WSMessage;

          // Dispatch to all listeners
          listenersRef.current.forEach((handler) => {
            try {
              handler(message);
            } catch (err) {
              console.error("[WebSocket] Listener error:", err);
            }
          });
        } catch (error) {
          console.error("[WebSocket] Error parsing message:", error);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        clearHeartbeat();

        // Exponential backoff reconnect
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(
            1000 * 2 ** reconnectAttempts.current,
            30000
          );
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.error("[WebSocket] Error:", error);
      };
    } catch (error) {
      console.error("[WebSocket] Failed to connect:", error);
    }
  }, [userId, userRole, clearHeartbeat]);

  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    clearHeartbeat();
    if (wsRef.current) {
      wsRef.current.close(1000, "Client disconnect");
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [clearReconnectTimeout, clearHeartbeat]);

  // Connect when userId is available, disconnect on cleanup
  useEffect(() => {
    if (userId && WS_URL) {
      connect();
    }
    return () => {
      disconnect();
    };
  }, [userId, connect, disconnect]);

  // Re-auth if userRole changes while connected
  useEffect(() => {
    if (userId && userRole && isConnected && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: "auth",
          payload: { userId, userRole },
        })
      );
    }
  }, [userId, userRole, isConnected]);

  return {
    isConnected,
    sendMessage,
    subscribe,
    unsubscribe,
    addMessageListener,
  };
}
